import type { OrderCalculation } from '../../../types/order-calculation';
import type { TicketOfficeView } from '../../../types/ticket-office';
import type { GiftCardType, ProductVariant, SerialTicketType } from '../../../types/types';
import { Api } from '../api';
import { updateCalc } from './update-calc';

interface CartItem {
    quantity: number;
    unitPrice?: number;
    typeId?: number;
}

type CartItems = Record<string, CartItem>;

export const removeProduct = (view: TicketOfficeView, variant: ProductVariant): void => {
    variant.quantity = 0;
    variant.unitPrice = variant.initialUnitPrice;
    view.update();
    syncProducts(view);
};

export const removeSerialTicketPurchase = (view: TicketOfficeView, type: SerialTicketType): void => {
    type.quantity = 0;
    view.update();
    syncProducts(view);
};

export const removeGiftCardPurchase = (view: TicketOfficeView, type: GiftCardType): void => {
    type.quantity = 0;
    view.update();
    syncProducts(view);
};

export const openProductModal = (view: TicketOfficeView, productVariant: ProductVariant) => {
    const { state, update } = view;

    state.modalQuantity = productVariant.quantity;
    state.modalUnitPrice = productVariant.unitPrice;
    state.summaryItemModalType = 'product';
    state.summaryItemModalProductVariantId = productVariant.id;
    update();
};

export const openSerialTicketModal = (view: TicketOfficeView, serialTicketType: SerialTicketType) => {
    const { state, update } = view;

    state.modalQuantity = serialTicketType.quantity;
    state.summaryItemModalType = 'serialTicket';
    state.summaryItemModalSerialTicketTypeId = serialTicketType.id;
    update();
};

export const openGiftCardModal = (view: TicketOfficeView, giftCardType: GiftCardType) => {
    const { state, update } = view;

    // Check if this is an open gift card
    if (giftCardType.allow_custom_value && giftCardType.unitPrice > 0) {
        // Open the custom amount modal for editing
        state.isOpenGiftCardModalOpen = true;
        state.openGiftCardModalType = giftCardType;
    } else {
        // Use standard modal for regular gift cards
        state.modalQuantity = giftCardType.quantity;
        state.modalUnitPrice = giftCardType.unitPrice;
        state.summaryItemModalType = 'giftCard';
        state.summaryItemModalGiftCardTypeId = giftCardType.id;
        // Store composite key for open gift cards to find the correct instance
        if (giftCardType.allow_custom_value) {
            state.summaryItemModalGiftCardCompositeKey = `${giftCardType.id}_${Math.round(giftCardType.unitPrice * 100)}`;
        } else {
            state.summaryItemModalGiftCardCompositeKey = null;
        }
    }
    update();
};

/**
 * Writes the box office's products to the cart and takes the server's new calculation. Returns the server's message
 * when it turns the cart back, such as for a new membership whose address the mail service would refuse; the cart
 * then stays as it was.
 */
export const syncProducts = async (view: TicketOfficeView): Promise<string | undefined> => {
    const { state } = view;
    const { csrfToken, locale, eventId, sessionId, products, serialTicketTypes, urls, giftCardTypes, cardTypes } =
        state;

    const cartProducts: CartItems = {};
    const cartSerialTickets: CartItems = {};
    const cartGiftCards: CartItems = {};
    const cartMemberships = state.purchasedMemberships || [];

    for (const product of products) {
        for (const variant of product.variants) {
            if (variant.quantity) {
                cartProducts[variant.id] = {
                    quantity: variant.quantity,
                };
            }
        }
    }

    for (const type of serialTicketTypes) {
        if (type.quantity) {
            cartSerialTickets[type.id] = {
                quantity: type.quantity,
            };
        }
    }

    for (const type of giftCardTypes) {
        if (type.quantity) {
            // For open gift cards with custom price, use composite key
            // Otherwise use simple type ID for regular gift cards
            const key =
                type.allow_custom_value && type.unitPrice > 0
                    ? `${type.id}_${Math.round(type.unitPrice * 100)}`
                    : type.id.toString();

            cartGiftCards[key] = {
                quantity: type.quantity,
                unitPrice: type.unitPrice,
                typeId: type.id, // Include typeId for backend parsing
            };
        }
    }

    const response = await Api.postJson(urls.setProducts, csrfToken, {
        sessionId,
        eventId,
        products: cartProducts,
        serialTickets: cartSerialTickets,
        giftCards: cartGiftCards,
        memberships: cartMemberships,
        locale,
    });

    if (!response.ok) {
        const refusal = (await response.json().catch(() => ({}))) as { error?: string };

        return refusal.error ?? state.lang.errors.submitFailed;
    }

    const data = (await response.json()) as { calc: OrderCalculation };
    updateCalc(view, data.calc);

    return undefined;
};
